import {
  DEFAULT_SCENARIO_LABELS,
  SCENARIO_TYPES,
  createScenarioId,
  emptyScenarioOverrides,
  nowIso,
  type Scenario,
  type ScenarioType,
  type SimulationGraphExtension,
  type SimulationTarget,
} from '../contracts';
import type { MetricInput } from '@/domain/metrics/contracts';
import { validateEdgeOverride, validateNodeOverride } from './validation';

/**
 * SPEC-009 §8/§48/plan §8/SIM-REQ-010 — the three canonical scenarios,
 * created empty. No arbitrary +/- percentage is ever invented here.
 */
export function createDefaultScenarios(now: string = nowIso()): Scenario[] {
  return (['conservative', 'probable', 'optimistic'] as const).map((type) => ({
    id: createScenarioId(),
    name: DEFAULT_SCENARIO_LABELS[type],
    type,
    overrides: emptyScenarioOverrides(),
    createdAt: now,
    updatedAt: now,
    isDefault: true,
  }));
}

/** Idempotently ensures the three canonical scenarios exist (plan §8: "on first simulation use"). */
export function ensureDefaultScenarios(extension: SimulationGraphExtension): SimulationGraphExtension {
  const existingTypes = new Set(extension.scenarios.filter((s) => s.isDefault).map((s) => s.type));
  const missing = (['conservative', 'probable', 'optimistic'] as const).filter((type) => !existingTypes.has(type));
  if (missing.length === 0) return extension;
  const now = nowIso();
  const created = missing.map((type) => ({
    id: createScenarioId(),
    name: DEFAULT_SCENARIO_LABELS[type],
    type,
    overrides: emptyScenarioOverrides(),
    createdAt: now,
    updatedAt: now,
    isDefault: true,
  }));
  return { ...extension, scenarios: [...extension.scenarios, ...created] };
}

export function createCustomScenario(
  extension: SimulationGraphExtension,
  name: string,
): { extension: SimulationGraphExtension; scenario: Scenario } {
  const now = nowIso();
  const scenario: Scenario = {
    id: createScenarioId(),
    name: name.trim() || 'Custom scenario',
    type: 'custom',
    overrides: emptyScenarioOverrides(),
    createdAt: now,
    updatedAt: now,
  };
  return { extension: { ...extension, scenarios: [...extension.scenarios, scenario] }, scenario };
}

/** SPEC-009 §49/§75 — duplication always yields a `custom` scenario, never a
 * second instance of a stable default type (§12: canonical types remain unique). */
export function duplicateScenario(
  extension: SimulationGraphExtension,
  scenarioId: string,
  name?: string,
): { extension: SimulationGraphExtension; scenario: Scenario } | null {
  const source = extension.scenarios.find((s) => s.id === scenarioId);
  if (!source) return null;
  const now = nowIso();
  const scenario: Scenario = {
    id: createScenarioId(),
    name: name?.trim() || `${source.name} (copy)`,
    type: 'custom',
    overrides: {
      nodeOverrides: { ...source.overrides.nodeOverrides },
      edgeOverrides: { ...source.overrides.edgeOverrides },
    },
    target: source.target,
    createdAt: now,
    updatedAt: now,
  };
  return { extension: { ...extension, scenarios: [...extension.scenarios, scenario] }, scenario };
}

export function renameScenario(extension: SimulationGraphExtension, scenarioId: string, name: string): SimulationGraphExtension {
  const trimmed = name.trim();
  if (!trimmed) return extension;
  return {
    ...extension,
    scenarios: extension.scenarios.map((s) => (s.id === scenarioId ? { ...s, name: trimmed, updatedAt: nowIso() } : s)),
  };
}

/**
 * SPEC-009 §51/§12 — a canonical default scenario is reset, not deleted,
 * through this API; only a non-default (custom) scenario can actually be
 * removed. Returns the extension unchanged if `scenarioId` is a default.
 */
export function deleteScenario(extension: SimulationGraphExtension, scenarioId: string): SimulationGraphExtension {
  const target = extension.scenarios.find((s) => s.id === scenarioId);
  if (!target || target.isDefault) return extension;
  const scenarios = extension.scenarios.filter((s) => s.id !== scenarioId);
  const activeScenarioId = extension.activeScenarioId === scenarioId ? null : extension.activeScenarioId;
  return { ...extension, scenarios, activeScenarioId };
}

/** SPEC-009 §52/SIM-REQ-011 — removes overrides, returning the scenario to base. */
export function resetScenario(extension: SimulationGraphExtension, scenarioId: string): SimulationGraphExtension {
  return {
    ...extension,
    scenarios: extension.scenarios.map((s) =>
      s.id === scenarioId ? { ...s, overrides: emptyScenarioOverrides(), updatedAt: nowIso() } : s,
    ),
  };
}

export function setActiveScenario(extension: SimulationGraphExtension, scenarioId: string | null): SimulationGraphExtension {
  return { ...extension, activeScenarioId: scenarioId };
}

export function setScenarioTarget(
  extension: SimulationGraphExtension,
  scenarioId: string,
  target: SimulationTarget | undefined,
): SimulationGraphExtension {
  return {
    ...extension,
    scenarios: extension.scenarios.map((s) => (s.id === scenarioId ? { ...s, target, updatedAt: nowIso() } : s)),
  };
}

export type NodeOverrideField = 'cost' | 'entries' | 'exits' | 'conversion' | 'revenue' | 'averageTicket';

/** SPEC-009 §86/FM-005 — validates before applying; an invalid value leaves the scenario untouched. */
export function setNodeOverrideField(
  extension: SimulationGraphExtension,
  scenarioId: string,
  nodeId: string,
  field: NodeOverrideField,
  value: MetricInput,
): SimulationGraphExtension {
  const candidate = validateNodeOverride({ [field]: value });
  if (!candidate) return extension;
  return {
    ...extension,
    scenarios: extension.scenarios.map((s) => {
      if (s.id !== scenarioId) return s;
      const nodeOverrides = { ...s.overrides.nodeOverrides };
      nodeOverrides[nodeId] = { ...nodeOverrides[nodeId], [field]: candidate[field] };
      return { ...s, overrides: { ...s.overrides, nodeOverrides }, updatedAt: nowIso() };
    }),
  };
}

export function clearNodeOverrideField(
  extension: SimulationGraphExtension,
  scenarioId: string,
  nodeId: string,
  field: NodeOverrideField,
): SimulationGraphExtension {
  return {
    ...extension,
    scenarios: extension.scenarios.map((s) => {
      if (s.id !== scenarioId) return s;
      const current = s.overrides.nodeOverrides[nodeId];
      if (!current) return s;
      const rest = { ...current };
      delete rest[field];
      const nodeOverrides = { ...s.overrides.nodeOverrides };
      if (Object.keys(rest).length === 0) delete nodeOverrides[nodeId];
      else nodeOverrides[nodeId] = rest;
      return { ...s, overrides: { ...s.overrides, nodeOverrides }, updatedAt: nowIso() };
    }),
  };
}

export function setEdgeOverrideConversion(
  extension: SimulationGraphExtension,
  scenarioId: string,
  edgeId: string,
  value: MetricInput,
): SimulationGraphExtension {
  const candidate = validateEdgeOverride({ conversion: value });
  if (!candidate) return extension;
  return {
    ...extension,
    scenarios: extension.scenarios.map((s) => {
      if (s.id !== scenarioId) return s;
      const edgeOverrides = { ...s.overrides.edgeOverrides };
      edgeOverrides[edgeId] = { ...edgeOverrides[edgeId], conversion: candidate.conversion };
      return { ...s, overrides: { ...s.overrides, edgeOverrides }, updatedAt: nowIso() };
    }),
  };
}

/** SPEC-009 plan §23 — reverseShare is a planning assumption stored per scenario, per edge. */
export function setEdgeReverseShare(
  extension: SimulationGraphExtension,
  scenarioId: string,
  edgeId: string,
  share: number,
): SimulationGraphExtension {
  const candidate = validateEdgeOverride({ reverseShare: share });
  if (!candidate) return extension;
  return {
    ...extension,
    scenarios: extension.scenarios.map((s) => {
      if (s.id !== scenarioId) return s;
      const edgeOverrides = { ...s.overrides.edgeOverrides };
      edgeOverrides[edgeId] = { ...edgeOverrides[edgeId], reverseShare: candidate.reverseShare };
      return { ...s, overrides: { ...s.overrides, edgeOverrides }, updatedAt: nowIso() };
    }),
  };
}

export function clearEdgeOverrideField(
  extension: SimulationGraphExtension,
  scenarioId: string,
  edgeId: string,
  field: 'conversion' | 'reverseShare',
): SimulationGraphExtension {
  return {
    ...extension,
    scenarios: extension.scenarios.map((s) => {
      if (s.id !== scenarioId) return s;
      const current = s.overrides.edgeOverrides[edgeId];
      if (!current) return s;
      const rest = { ...current };
      delete rest[field];
      const edgeOverrides = { ...s.overrides.edgeOverrides };
      if (Object.keys(rest).length === 0) delete edgeOverrides[edgeId];
      else edgeOverrides[edgeId] = rest;
      return { ...s, overrides: { ...s.overrides, edgeOverrides }, updatedAt: nowIso() };
    }),
  };
}

export function isCanonicalScenarioType(type: ScenarioType): boolean {
  return SCENARIO_TYPES.includes(type) && type !== 'custom';
}
